import { Appointment, AppointmentStatus } from '@packages/core-shared';
import { useEffect, useMemo, useState } from 'react';
import { useLoadPatientAppointments } from './useLoadPatientAppointments';

/***
 * Loads professionals seen by patient email.
 * Doctor might provide multiple services, so Healee attaches multiple services to the appoitment.
 * It results in multiple doctor records in PDFs.
 * If the appointment service is among recommnded, then the rest services will be removed.
 * Returns Map<serviceId, appointment with modified services>
 */
export const useLoadProfessionalsSeen = ({
  patientEmail,
  recommendedServices,
}: {
  patientEmail?: string;
  recommendedServices: number[];
}) => {
  const appointmentsLoader = useLoadPatientAppointments({
    patientEmail,
    status: AppointmentStatus.completed,
  });

  const [professionalsSeen, setProfessionalsSeen] = useState<
    Record<string, Appointment[]>
  >({});

  useEffect(() => {
    const apps = appointmentsLoader.records
      // filter out missed appointments
      .filter((a) => !a.noShow)
      // filter services that were not recommended
      .map((app) => {
        const recommendedService = app.services?.find(
          (s) => !!s && recommendedServices.includes(s),
        );
        if (recommendedService) {
          return {
            ...app,
            services: app.services?.filter((s) => s === recommendedService),
          };
        } else {
          return app;
        }
      });

    let servicesVisited = apps.flatMap(
      (app) =>
        app.services?.filter((s) => !!s)?.map((s) => s!.toString()) ?? [],
    );
    // unique
    servicesVisited = servicesVisited.filter(
      (value, index, array) => array.indexOf(value) === index,
    );

    const aggregatedServices = servicesVisited.reduce(
      (acc, serviceId) => {
        acc[serviceId] = apps.filter((app) =>
          app.services?.map((s) => s?.toString())?.includes(serviceId),
        );

        return acc;
      },
      {} as Record<string, Appointment[]>,
    );

    setProfessionalsSeen(aggregatedServices);
  }, [appointmentsLoader.records]);

  const result = useMemo(() => {
    return {
      professionalsSeen,
      loading: appointmentsLoader.loading,
      errorMessage: appointmentsLoader.errorMessage,
    };
  }, [
    professionalsSeen,
    appointmentsLoader.loading,
    appointmentsLoader.errorMessage,
  ]);

  return result;
};
